/**
 * Θεματολόγιο — the department's diary of planned topics/assignments (HPress module THEMATA).
 * Search screen in the HPress arrangement (date range, topic text, category, time, reporter,
 * records), results sorted by date and time, create/edit in a dialog with optimistic saves.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError, del, get, post, put } from '../api.js';
import { PageTitle } from '../frame.js';
import { currentLocale, t } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { navigate } from '../router.js';
import { useSession } from '../session.js';
import { fmtDateInput, fmtDateTime, todayIso } from '../util.js';
import { Dialog } from './rundown-dialogs.js';

export interface TopicDto {
  id: string;
  date: string;
  time: string | null;
  title: string;
  categoryId: string | null;
  categoryName: string | null;
  reporter: string | null;
  appointment: string | null;
  remarks: string | null;
  comments: string | null;
  originUserId: string | null;
  originName: string | null;
  originText: string | null;
  revision: number;
  createdAt: string;
  createdById: string | null;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  canEdit: boolean;
  canDelete: boolean;
}
interface Options {
  categories: Array<{ id: string; name: string; active: boolean }>;
  people: Array<{ id: string; displayName: string }>;
}
interface ListResult {
  from: string;
  to: string;
  limit: number;
  more: boolean;
  topics: TopicDto[];
}
interface Revision {
  revision: number;
  action: string;
  snapshot: Record<string, unknown>;
  savedAt: string;
  savedBy: string | null;
}

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const weekday = (iso: string) => new Intl.DateTimeFormat(currentLocale() === 'el' ? 'el-GR' : 'en-GB', { weekday: 'long' }).format(new Date(`${iso}T00:00:00`));

export function TopicsPage({ openNew = false }: { openNew?: boolean }) {
  const { can } = useSession();
  const today = todayIso();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [reporter, setReporter] = useState('');
  const [limit, setLimit] = useState(100);
  const [data, setData] = useState<ListResult | null>(null);
  const [options, setOptions] = useState<Options>({ categories: [], people: [] });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ mode: 'new' } | { mode: 'edit'; topic: TopicDto } | null>(openNew ? { mode: 'new' } : null);

  const load = useCallback(
    (range?: { from: string; to: string }) => {
      const f = range?.from ?? from;
      const tt = range?.to ?? to;
      const params = new URLSearchParams({ from: f, to: tt, limit: String(limit) });
      if (q.trim()) params.set('q', q.trim());
      if (categoryId) params.set('categoryId', categoryId);
      if (reporter.trim()) params.set('reporter', reporter.trim());
      setError(null);
      get<ListResult>(`/api/topics?${params}`)
        .then(setData)
        .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    },
    [from, to, q, categoryId, reporter, limit],
  );

  useEffect(() => {
    get<Options>('/api/topics/options')
      .then(setOptions)
      .catch(() => undefined);
    const id = setTimeout(() => load(), 0);
    return () => clearTimeout(id);
    // initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    load();
  };
  const quick = (f: string, tt: string) => {
    setFrom(f);
    setTo(tt);
    load({ from: f, to: tt });
  };
  const clear = () => {
    setFrom(today);
    setTo(today);
    setQ('');
    setCategoryId('');
    setReporter('');
    setLimit(100);
  };

  const groups = useMemo(() => {
    const m = new Map<string, TopicDto[]>();
    for (const x of data?.topics ?? []) m.set(x.date, [...(m.get(x.date) ?? []), x]);
    return [...m.entries()];
  }, [data]);

  const closeDialog = () => {
    setDialog(null);
    if (openNew) navigate('/topics');
  };
  const onSaved = (topic: TopicDto, created: boolean) => {
    setNotice(t('topicSaved'));
    setTimeout(() => setNotice(null), 2500);
    closeDialog();
    if (created && (topic.date < from || topic.date > to)) quick(topic.date, topic.date);
    else load();
  };
  const remove = async (x: TopicDto) => {
    if (!window.confirm(t('confirmDeleteTopic'))) return;
    try {
      await del(`/api/topics/${x.id}`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const cols = 10;
  return (
    <>
      <PageTitle title={t('topicsSearchTitle')} hint={t('searchHint')} />
      <form className="panel" onSubmit={submit}>
        <div className="panel-head">
          <span className="panel-title">{t('topicDetails')}</span>
          <div className="toolbar">
            <button type="button" className="btn" onClick={() => quick(today, today)}>
              {t('today')}
            </button>
            <button type="button" className="btn" onClick={() => quick(addDays(today, 1), addDays(today, 1))}>
              {t('tomorrow')}
            </button>
            <button type="button" className="btn" onClick={() => quick(today, addDays(today, 6))}>
              {t('thisWeek')}
            </button>
            {can('topic.create') && (
              <button type="button" className="btn primary" onClick={() => setDialog({ mode: 'new' })}>
                ＋ {t('topicNew')}
              </button>
            )}
          </div>
        </div>
        <div className="form-grid">
          <label htmlFor="tf">{t('topicDate')} :</label>
          <div className="range">
            <span>{t('fromDate')} :</span>
            <DateInput id="tf" value={from} onChange={setFrom} />
            <span>{t('toDate')} :</span>
            <DateInput value={to} onChange={setTo} />
          </div>
          <label htmlFor="tq">{t('topic')} :</label>
          <input id="tq" type="text" value={q} onChange={(e) => setQ(e.target.value)} />
          <label htmlFor="tc">{t('category')} :</label>
          <select id="tc" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">{t('anyCategory')}</option>
            {options.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.active ? c.name : `* ${c.name}`}
              </option>
            ))}
          </select>
          <label htmlFor="tr">{t('reporter')} :</label>
          <input id="tr" type="text" value={reporter} onChange={(e) => setReporter(e.target.value)} />
          <label htmlFor="tl">{t('records')} :</label>
          <select id="tl" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            {[20, 40, 80, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span />
          <div className="toolbar left">
            <button className="btn primary" type="submit">
              🔍 {t('search')}
            </button>
            <button className="btn" type="button" onClick={clear}>
              {t('clear')}
            </button>
          </div>
        </div>
      </form>
      <p className="tip">{t('topicsHint')}</p>
      {error && <div className="banner err">{error}</div>}
      {notice && <div className="banner info">{notice}</div>}
      {data && (
        <div className="panel">
          <div className="infobar">
            <span>
              {fmtDateInput(data.from)}
              {data.to !== data.from ? ` – ${fmtDateInput(data.to)}` : ''}
            </span>
            <span className="muted">
              {data.topics.length} {t('topicsCount')}
            </span>
          </div>
          {data.more && <div className="banner warn">{t('topicsMore')}</div>}
          <table className="grid topics">
            <thead>
              <tr>
                <th style={{ width: 54 }}>{t('topicTime')}</th>
                <th>{t('topic')}</th>
                <th>{t('category')}</th>
                <th>{t('reporter')}</th>
                <th>{t('appointment')}</th>
                <th>{t('remarks')}</th>
                <th>{t('comments')}</th>
                <th>{t('origin')}</th>
                <th>{t('updatedAt')}</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr>
                  <td colSpan={cols} className="c muted">
                    {t('topicsNone')}
                  </td>
                </tr>
              )}
              {groups.map(([date, rows]) => (
                <GroupRows key={date} date={date} rows={rows} cols={cols} onEdit={(x) => setDialog({ mode: 'edit', topic: x })} onDelete={remove} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog && <TopicDialog mode={dialog.mode} topic={dialog.mode === 'edit' ? dialog.topic : null} options={options} defaultDate={from} onClose={closeDialog} onSaved={onSaved} />}
    </>
  );
}

function GroupRows({ date, rows, cols, onEdit, onDelete }: { date: string; rows: TopicDto[]; cols: number; onEdit: (x: TopicDto) => void; onDelete: (x: TopicDto) => void }) {
  return (
    <>
      <tr className="day">
        <td colSpan={cols}>
          <b>{weekday(date)}</b> {fmtDateInput(date)} <span className="muted">· {rows.length}</span>
        </td>
      </tr>
      {rows.map((x) => (
        <tr key={x.id}>
          <td className="num">{x.time ?? ''}</td>
          <td className="title">
            <a onClick={() => onEdit(x)} role="button" title={x.canEdit ? t('editRecord') : t('storyViewHint')}>
              {x.title}
            </a>
          </td>
          <td>{x.categoryName ?? ''}</td>
          <td>{x.reporter ?? ''}</td>
          <td>{x.appointment ?? ''}</td>
          <td className="wrap">{x.remarks ?? ''}</td>
          <td className="wrap">{x.comments ?? ''}</td>
          <td>{x.originName ?? x.originText ?? ''}</td>
          <td className="num small">
            {fmtDateTime(x.updatedAt)}
            {x.updatedBy ? <div className="muted">{x.updatedBy}</div> : null}
          </td>
          <td className="actions">
            {x.canEdit && <button className="icon-edit" title={t('editRecord')} onClick={() => onEdit(x)} />}
            {x.canDelete && <button className="icon-delete" title={t('delete')} onClick={() => onDelete(x)} />}
          </td>
        </tr>
      ))}
    </>
  );
}

interface FormState {
  date: string;
  time: string;
  title: string;
  categoryId: string;
  reporter: string;
  appointment: string;
  remarks: string;
  comments: string;
  originUserId: string;
  originText: string;
}
const toForm = (x: TopicDto | null, defaultDate: string, meId: string | null): FormState => ({
  date: x?.date ?? defaultDate,
  time: x?.time ?? '',
  title: x?.title ?? '',
  categoryId: x?.categoryId ?? '',
  reporter: x?.reporter ?? '',
  appointment: x?.appointment ?? '',
  remarks: x?.remarks ?? '',
  comments: x?.comments ?? '',
  originUserId: x ? (x.originUserId ?? '') : (meId ?? ''),
  originText: x?.originText ?? '',
});

function TopicDialog({ mode, topic, options, defaultDate, onClose, onSaved }: { mode: 'new' | 'edit'; topic: TopicDto | null; options: Options; defaultDate: string; onClose: () => void; onSaved: (t: TopicDto, created: boolean) => void }) {
  const { session } = useSession();
  const [form, setForm] = useState<FormState>(() => toForm(topic, defaultDate, session?.user.id ?? null));
  const [baseRevision, setBaseRevision] = useState(topic?.revision ?? 0);
  const [history, setHistory] = useState<Revision[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const readOnly = mode === 'edit' && !topic?.canEdit;
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (topic) get<{ topic: TopicDto; revisions: Revision[] }>(`/api/topics/${topic.id}`).then((r) => setHistory(r.revisions)).catch(() => undefined);
  }, [topic]);

  const payload = () => ({
    date: form.date,
    time: form.time.trim() || null,
    title: form.title.trim(),
    categoryId: form.categoryId || null,
    reporter: form.reporter.trim() || null,
    appointment: form.appointment.trim() || null,
    remarks: form.remarks.trim() || null,
    comments: form.comments.trim() || null,
    originUserId: form.originUserId || null,
    originText: form.originText.trim() || null,
  });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'new') {
        const r = await post<{ topic: TopicDto }>('/api/topics', payload());
        onSaved(r.topic, true);
      } else {
        const r = await put<{ topic: TopicDto }>(`/api/topics/${topic!.id}`, { ...payload(), baseRevision });
        onSaved(r.topic, false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.payload && typeof err.payload === 'object' && 'current' in err.payload) {
        const cur = (err.payload as { current: TopicDto }).current;
        setForm(toForm(cur, defaultDate, null));
        setBaseRevision(cur.revision);
        setError(t('topicConflict'));
      } else setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={mode === 'new' ? t('topicNew') : t('topicEdit')} onClose={onClose} width={760}>
      <form onSubmit={save} className="topic-form">
        <div className="form-grid">
          <label className="req" htmlFor="td">
            {t('topicDate')} :
          </label>
          <DateInput id="td" required value={form.date} onChange={(v) => set('date')({ target: { value: v } })} disabled={readOnly} />
          <label htmlFor="tt">{t('topicTime')} :</label>
          <input id="tt" type="time" value={form.time} onChange={set('time')} disabled={readOnly} />
          <label className="req" htmlFor="tti">
            {t('topic')} :
          </label>
          <input id="tti" className="wide" type="text" required maxLength={300} value={form.title} onChange={set('title')} disabled={readOnly} autoFocus />
          <label htmlFor="tca">{t('category')} :</label>
          <select id="tca" value={form.categoryId} onChange={set('categoryId')} disabled={readOnly}>
            <option value="">{t('anyCategory')}</option>
            {options.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.active ? c.name : `* ${c.name}`}
              </option>
            ))}
          </select>
          <label htmlFor="tre">{t('reporter')} :</label>
          <input id="tre" type="text" maxLength={300} value={form.reporter} onChange={set('reporter')} disabled={readOnly} />
          <label htmlFor="tap">{t('appointment')} :</label>
          <input id="tap" className="wide" type="text" maxLength={300} value={form.appointment} onChange={set('appointment')} disabled={readOnly} />
          <label htmlFor="tpa">{t('remarks')} :</label>
          <textarea id="tpa" className="wide" rows={3} value={form.remarks} onChange={set('remarks')} disabled={readOnly} />
          <label htmlFor="tco">{t('comments')} :</label>
          <textarea id="tco" className="wide" rows={2} value={form.comments} onChange={set('comments')} disabled={readOnly} />
          <label htmlFor="top">{t('originPerson')} :</label>
          <select id="top" value={form.originUserId} onChange={set('originUserId')} disabled={readOnly}>
            <option value="">{t('nobody')}</option>
            {options.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </select>
          <label htmlFor="tot">{t('originFree')} :</label>
          <input id="tot" type="text" maxLength={600} value={form.originText} onChange={set('originText')} disabled={readOnly} />
        </div>
        {error && <div className="banner err">{error}</div>}
        <div className="toolbar">
          {topic && (
            <span className="muted" style={{ marginRight: 'auto' }}>
              {t('createdBy')}: {topic.createdBy ?? '—'} · {t('createdAt')}: {fmtDateTime(topic.createdAt)} · {t('revision')} {topic.revision}
            </span>
          )}
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          {!readOnly && (
            <button type="submit" className="btn primary" disabled={busy || !form.title.trim() || !form.date}>
              {t('save')}
            </button>
          )}
        </div>
      </form>
      {topic && history.length > 0 && (
        <details className="history">
          <summary>
            {t('history')} ({history.length})
          </summary>
          <table className="grid compact">
            <thead>
              <tr>
                <th>{t('revision')}</th>
                <th>{t('action')}</th>
                <th>{t('when')}</th>
                <th>{t('by')}</th>
                <th>{t('topic')}</th>
                <th>{t('topicTime')}</th>
                <th>{t('reporter')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.revision}>
                  <td className="num">{h.revision}</td>
                  <td>{h.action}</td>
                  <td className="num">{fmtDateTime(h.savedAt)}</td>
                  <td>{h.savedBy ?? '—'}</td>
                  <td className="title">{String(h.snapshot.title ?? '')}</td>
                  <td className="num">{String(h.snapshot.time ?? '')}</td>
                  <td>{String(h.snapshot.reporter ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Dialog>
  );
}
